'use client';

import { useEffect, useLayoutEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import {
  initPostHog,
  applyCapture,
  applyReplay,
  applyAutocapture,
  applyPersistence,
} from './posthog';
import { captureFirstTouch } from './first-touch';
import { captureCurrentPageView } from './pageview';
import { CONSENT_CHANGED_EVENT, REPLAY_CONSENT_CHANGED_EVENT } from './consent';
import { USER_OPTOUT_CHANGED_EVENT } from './user-optout';

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Navigation pageview. Before consent this returns without remembering
    // the URL, so the accept handler below can still send the landing page.
    captureCurrentPageView();
  }, [pathname, searchParams]);

  // On every route change, re-apply route-dependent state: autocapture (off
  // on public routes), general capture, and replay (suppressed on sensitive
  // routes). Persistence follows consent, not the route, and is a no-op when
  // the mode is already correct — calling set_config with a new mode clears
  // the stored distinct id.
  useEffect(() => {
    applyPersistence(pathname);
    applyAutocapture(pathname);
    applyCapture();
    applyReplay(pathname);
  }, [pathname]);

  // React to consent and user-opt-out changes mid-session so a decline,
  // acceptance, or account-level objection takes effect immediately.
  // Acceptance is also when persistence may move from memory to cookie.
  useEffect(() => {
    const onChange = () => {
      applyPersistence(pathname);
      applyAutocapture(pathname);
      applyCapture();
      applyReplay(pathname);
      // Consent accept is the first moment capture is allowed. The mount
      // pageview was dropped; send this URL once, with UTMs and referrer.
      // Later navigations are the effect above and do not repeat this URL.
      captureCurrentPageView({ recoverFirstTouch: true });
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    window.addEventListener(REPLAY_CONSENT_CHANGED_EVENT, onChange);
    window.addEventListener(USER_OPTOUT_CHANGED_EVENT, onChange);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
      window.removeEventListener(REPLAY_CONSENT_CHANGED_EVENT, onChange);
      window.removeEventListener(USER_OPTOUT_CHANGED_EVENT, onChange);
    };
  }, [pathname]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useLayoutEffect(() => {
    // layout, not effect: child pageviews and funnel events run in useEffect.
    // Init has to finish (and opt in, when consent already exists) before those
    // captures, or they are dropped by opt_out_capturing_by_default.
    captureFirstTouch();
    initPostHog();
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </>
  );
}
